/**
 * Checks whether the given string is empty.
 *
 * @param {string|undefined|null} str - The string to check.
 * @return {boolean} - True if the passed-in string is undefined, null or has length 0. False otherwise.
 */
export const isEmptyString = (str) => {
  return str === undefined || str === null || str.length === 0;
};

/**
 * Change the first letter of the passed string value to uppercase
 *
 * @param {string} string - The string need to be transformed.
 * @returns {string} - The string with first letter changed to upper case
 */
export const firstLetterToUpperCase = (string) => {
  if (!isEmptyString(string)) {
    if (string) return string.charAt(0).toUpperCase() + string.slice(1);
  }
  return null;
};

/**
 * Converts the given string to Sentence case.
 *
 * @param {string} string - The string to be transformed.
 * @returns - The string converted to Sentence case.
 */
export const toSentenceCase = (string) => {
  if (typeof string !== 'string') return string;
  if (string === '') return string;

  return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
};

/**
 * Splits the string with seperator of ',' into an array of strings, returns null if string is null
 *
 * @param {string} string - The string needed to be transformed.
 * @returns {array} - The array containing strings separated by ','
 */
export const splitString = (string) => {
  if (string) {
    return string.split(',');
  }
  return null;
};

/**
 * Concatenates the value at each array index into a single string.
 *
 * @param {array} array - An array of values.
 * @returns {string} - A delimited string representing all the values in the array.
 */
export const concatenateStrings = (array) => {
  if (!Array.isArray(array)) return array;

  return array.join(', ');
};

/**
 * remove whitespace from a string
 *
 * @param {string} str - The string needed to be transformed.
 * @returns {string} - The string without whitespace
 */
export const removeWhitespaceFromString = (str) => {
  if (!isEmptyString(str)) {
    return str.replace(/\s/g, '');
  }
  return null;
};

/**
 * replaces one  or more whitespace or new lines with a single space and trims leading  and trailing whitespace
 *
 * @param {string} str - The string needed to be transformed.
 * @example
 * // returns 'this is a string'
 * replaceWhitespaceWithSingleSpace('  this   is a    string   ')
 * @returns {string} The string with one or more whitespaces replaced with a single space
 *
 */
export const replaceWhitespaceWithSingleSpace = (str) => {
  return str.replace(/\s+/g, ' ').trim();
};

/**
 * Format BSB with "### - ###" mask
 *
 * @param {string} str - BSB number either in `######` or `###-###` format.
 * @returns {string|null} - BSB with mask "### - ###" (or null if it fails)
 */
export const BSBMask = (str) => {
  if (!isEmptyString(str)) {
    const sixDigitsRegex = /^\d{6}$/; // regex to check if bsb in format `######` where all #s are digits
    const dashSeparatedDigits = /^\d{3}-\d{3}$/; // regex to check if bsb in format `###-###` where all #s are digits
    if (sixDigitsRegex.test(str)) {
      return `${str.substring(0, 3)} - ${str.substring(3)}`;
    }
    if (dashSeparatedDigits.test(str)) {
      return str.replace('-', ' - ');
    }
  }
  return null;
};

/**
 * remove the " - " in BSB value
 *
 * @param {string} str - Masked BSB number e.g."### - ###".
 * @returns {string} - BSB with numbers only e.g "######"
 */
export const removeBSBMask = (str) => {
  if (!isEmptyString(str)) {
    return str.split(' - ').join('');
  }
  return null;
};

/**
 * remove <p></p> /r/n
 *
 * @param {string}  - paragraph with <p></p> /r/n
 * @returns {string} - remove <p></p> /r/n and return text only
 */
export const getPlainParagraphText = (paragraph) => {
  if (!isEmptyString(paragraph)) {
    return paragraph
      .replace(/<p[^>]*>/g, '')
      .replace(/<\/p>/g, '')
      .replace(/<br>/g, '')
      .replace(/(\r\n|\n|\r)/g, '');
  }
  return '';
};

/**
 * Convert 10 digits medicare number into the format of "XXXX XXXXX X"
 *
 * @param {string} cardNumber - Medicare card number, 10 digits
 * @returns {string} - Card number in XXXX XXXXX X format, and if the number is not or less than 10 digits long, return plain number string without any whitespaces
 */
export const formatMedicareCardNumber = (cardNumber) => {
  if (!isEmptyString(cardNumber)) {
    return cardNumber.replace(/^(\w{4})(\w{5})(.*)$/, '$1 $2 $3');
  }
  return '';
};
